/**
 * Client-side table sort, search filter, and pagination for React tables.
 */

import { useMemo, useState, useCallback, useEffect } from 'react'

function compareValues(a, b, dir) {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1

  if (a instanceof Date && b instanceof Date) {
    const diff = a.getTime() - b.getTime()
    return dir === 'asc' ? diff : -diff
  }

  const an = Number(a)
  const bn = Number(b)
  if (!Number.isNaN(an) && !Number.isNaN(bn)) {
    const diff = an - bn
    return dir === 'asc' ? diff : -diff
  }

  const diff = String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
  return dir === 'asc' ? diff : -diff
}

/**
 * @param {Array} items
 * @param {object} [options]
 * @param {number} [options.pageLength=10]
 * @param {{ key: string|null, dir: 'asc'|'desc' }} [options.initialSort]
 * @param {string} [options.searchText]
 * @param {(item: unknown, searchText: string) => boolean} [options.filterItem]
 * @param {(item: unknown, sortKey: string) => unknown} [options.getSortValue]
 * @param {unknown[]} [options.resetSortDeps] - when deps change, reset sort to initialSort
 */
export function useClientDatatable(items, options = {}) {
  const {
    pageLength = 10,
    initialSort = { key: null, dir: 'asc' },
    searchText = '',
    filterItem = null,
    getSortValue = (item, key) => item?.[key],
    resetSortDeps = [],
  } = options

  const [sort, setSort] = useState(initialSort)
  const [page, setPage] = useState(0)

  useEffect(() => {
    setSort(initialSort)
    setPage(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when tab/context changes
  }, resetSortDeps)

  useEffect(() => {
    setPage(0)
  }, [searchText, items.length, sort.key, sort.dir])

  const toggleSort = useCallback((key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      }
      return { key, dir: 'asc' }
    })
  }, [])

  const filteredItems = useMemo(() => {
    const list = items || []
    if (!searchText.trim() || !filterItem) return list
    const q = searchText.trim().toLowerCase()
    return list.filter((item) => filterItem(item, q))
  }, [items, searchText, filterItem])

  const sortedItems = useMemo(() => {
    if (!sort.key) return filteredItems
    const sorted = [...filteredItems]
    sorted.sort((a, b) => {
      const av = getSortValue(a, sort.key)
      const bv = getSortValue(b, sort.key)
      return compareValues(av, bv, sort.dir)
    })
    return sorted
  }, [filteredItems, sort.key, sort.dir, getSortValue])

  const pageCount = Math.max(1, Math.ceil(sortedItems.length / pageLength) || 1)
  const safePage = Math.min(page, pageCount - 1)

  useEffect(() => {
    if (page !== safePage) setPage(safePage)
  }, [page, safePage])

  const rows = useMemo(() => {
    const start = safePage * pageLength
    return sortedItems.slice(start, start + pageLength)
  }, [sortedItems, safePage, pageLength])

  const showingFrom = sortedItems.length === 0 ? 0 : safePage * pageLength + 1
  const showingTo = Math.min((safePage + 1) * pageLength, sortedItems.length)

  const goToPage = useCallback(
    (nextPage) => {
      setPage(Math.max(0, Math.min(nextPage, pageCount - 1)))
    },
    [pageCount],
  )

  return {
    rows,
    sortedItems,
    sort,
    toggleSort,
    page: safePage,
    pageCount,
    pageLength,
    totalItems: sortedItems.length,
    showingFrom,
    showingTo,
    goToPage,
    goPrev: () => goToPage(safePage - 1),
    goNext: () => goToPage(safePage + 1),
    hasPrev: safePage > 0,
    hasNext: safePage < pageCount - 1,
    showPagination: sortedItems.length > pageLength,
    hasItems: sortedItems.length > 0,
  }
}
